import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Button from '@components/button/Button'
import Header from '@components/header/Header'
import TextInput from '@components/textInput/TextInput'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import Icon from '@components/icon/Icons'

import api from '@api/api'
import logger from '@utils/logger'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import { orderTenants } from './tenantsUtils'

import sanitize from '@utils/Sanitizer'
import theme from '@theme/Theme'

const displayTenant = (tenant: LocatairePermanentResponse, search: string) => {
  if (!search) {
    return true
  }
  const cleanSearch = sanitize(search)
  return (
    sanitize(tenant.nomLocatairePermanent).includes(cleanSearch) ||
    !!tenant.locaux.find(
      (local) => sanitize(local.libelle).includes(cleanSearch) || sanitize(local.libelle).includes(cleanSearch)
    )
  )
}

const TenantsScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)

  const [search, setSearch] = React.useState('')

  const tabs: { label: string; value: keyof OrderedTenants }[] = [
    { label: i18n.t('screens.tenants.current'), value: 'current' },
    { label: i18n.t('screens.tenants.incoming'), value: 'incoming' },
    { label: i18n.t('screens.tenants.former'), value: 'former' },
  ]
  const [tab, setTab] = React.useState(tabs[0])

  const [{ width }, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  React.useEffect(() => {
    api.locatairePermanent
      .all(site.id)
      .then((res) => SiteStore.actions.setTenants(orderTenants(res)))
      .catch((err) => {
        logger.error(err)
        SiteStore.actions.resetTenants()
      })
  }, [])

  const displayedTenants: Tenant[] = React.useMemo(
    () => tenants[tab.value].filter((t) => displayTenant(t.tenant, search)),
    [tenants, search, tab]
  )

  const buttons = [
    {
      label: i18n.t('screens.site.startIncident'),
      onClick: () => navigation.push('/incident/start'),
    },
  ]

  const headerLinks = [
    {
      label: i18n.t(`screens.site.sites.${site.codeLieu}`),
      onClick: () => navigation.push('/'),
    },
    {
      label: i18n.t('screens.tenants.title'),
      onClick: () => undefined,
    },
  ]

  const renderPhoneSearch = () => (
    <InputContainer>
      <TextInput
        searchStyle="onDark"
        value={search}
        onChange={setSearch}
        placeholder={i18n.t('screens.tenants.placeholder')}
        left={{ name: 'search' }}
      />
    </InputContainer>
  )

  const renderBottomButton = isPhone
    ? () => (
        <BottomButton width={width}>
          <Button status="active" label={buttons[0].label} onClick={buttons[0].onClick} />
        </BottomButton>
      )
    : undefined

  const renderTenant = ({ tenant, dates }: Tenant) => (
    <TenantCard
      key={tenant.id}
      activeOpacity={0.9}
      onPress={() => navigation.push(`/tenant/${tenant.id}`, { tenantId: tenant.id })}>
      <TenantInfos>
        <TenantName>{tenant.nomLocatairePermanent}</TenantName>
        {dates.status && dates.start && dates.end && (
          <TenantDate>
            {i18n.t(`screens.tenants.${dates.status}Date`, {
              date: dates.status === 'former' ? dates.end : dates.start,
            })}
          </TenantDate>
        )}
      </TenantInfos>
      <Icon name="arrow" size={24} color={theme.colors.primary} />
    </TenantCard>
  )

  return (
    <Root
      header={i18n.t('screens.tenants.title')}
      renderFixedBottom={renderBottomButton}
      renderSubHeader={renderPhoneSearch}
      onLeft={navigation.goBack}>
      {!isPhone && <Header links={headerLinks} title={i18n.t('screens.tenants.title')} buttons={buttons} />}
      <MainContainer>
        {!isPhone && (
          <SearchContainer>
            <TextInput
              value={search}
              onChange={setSearch}
              placeholder={i18n.t('screens.tenants.placeholderLong')}
              left={{ name: 'search' }}
            />
          </SearchContainer>
        )}
        <SegmentedContainer>
          <SegmentedControl data={tabs} value={tab} onItemSelected={setTab} />
        </SegmentedContainer>
        {displayedTenants.length > 0 ? (
          <TenantList>{displayedTenants.map(renderTenant)}</TenantList>
        ) : (
          <MissingInfo>{i18n.t('screens.tenants.noTenant')}</MissingInfo>
        )}
      </MainContainer>
    </Root>
  )
}

export default TenantsScreen

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `16px` : '24px')};
`
const InputContainer = styled(View)`
  margin-top: 16px;
`
const SegmentedContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin-bottom: 16px;` : `margin: 24px 0px;`)}
`
const BottomButton = styled(View)<{ width: number }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background}E6;
  padding: 16px;
`
const TenantList = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`}
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const TenantCard = styled(TouchableOpacity)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin-bottom: 8px;' : 'margin: 0px 16px 16px 0px;')}

  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '16px 24px')};
  border-radius: 6px;
  background-color: ${(props) => props.theme.colors.background};
  flex-direction: row;
  align-items: center;
  ${(props) => props.theme.constants.shadow};
  ${(props) => props.theme.breakpoint !== 'phone' && `min-width: 360px;`}
`
const TenantInfos = styled(View)`
  flex: 1;
`
const TenantName = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
  ${(props) => props.theme.breakpoint !== 'phone' && `margin-bottom: 8px;`}
`
const SearchContainer = styled(View)`
  margin-bottom: 16px;
`
const TenantDate = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
`
